import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend
} from "recharts";
import { Card, OverlayTrigger, Tooltip, Badge, Modal, Button } from "react-bootstrap"; 
import { FiInfo, FiX } from "react-icons/fi";

const GraficoOrdiniFornitori = () => {
  const [dataGrafico, setDataGrafico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((json) => {
        const ordiniRaw = json?.ORDINI_URGENTI?.flat() || [];

        const raggruppati = ordiniRaw
          .filter(item => item["Tipo Riga"] === "T")
          .reduce((acc, curr) => {
            const nome = curr["Ragione sociale"] || "Sconosciuto";
            const importoTotale = parseFloat(curr["Importo"]) || 0;
            const qtaEva = parseFloat(curr["Qta EVA"]) || 0;
            const qtaDev = parseFloat(curr["Qta DEV"]) || 0;
            
            const qtaTotale = qtaEva + qtaDev;
            
            // Proporzioniamo l'importo in base alle quantità EVA e DEV
            let importoEva = 0;
            let importoDev = 0;

            if (qtaTotale > 0) {
              importoEva = importoTotale * (qtaEva / qtaTotale);
              importoDev = importoTotale * (qtaDev / qtaTotale);
            } else {
              // Fallback se le quantità sono entrambe a 0 ma c'è un importo
              importoEva = importoTotale;
            }

            if (!acc[nome]) {
              acc[nome] = { totale: 0, eva: 0, dev: 0 };
            }

            acc[nome].totale += importoTotale;
            acc[nome].eva += importoEva;
            acc[nome].dev += importoDev;

            return acc;
          }, {});

        const formattedData = Object.keys(raggruppati)
          .map((key) => ({
            name: key,
            totaleImporto: parseFloat(raggruppati[key].totale.toFixed(2)),
            importoEva: parseFloat(raggruppati[key].eva.toFixed(2)),
            importoDev: parseFloat(raggruppati[key].dev.toFixed(2)),
          }))
          .sort((a, b) => b.totaleImporto - a.totaleImporto)
          .slice(0, 10);

        setDataGrafico(formattedData);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore fetch grafico:", err);
        setLoading(false);
      });
  }, []);

  const formatXAxis = (tickItem) => {
    return tickItem.length > 12 ? `${tickItem.substring(0, 12)}...` : tickItem;
  };

  const renderTooltipSpiegazione = (props) => (
    <Tooltip id="info-grafico-tooltip" {...props} style={{ ...props.style, fontSize: '0.85rem' }}>
      Questo grafico mostra i <strong>top 10 fornitori</strong> per importo totale degli ordini urgenti. 
      Le barre sono divise in <span style={{color: '#2463eb'}}>Evase (Blu)</span> e <span style={{color: '#dc2626'}}>Deviate (Rosso)</span> in base alle rispettive quantità.
    </Tooltip>
  );

  // Contenuto del grafico riutilizzabile sia nella card che nel modale
  const renderChartContent = (isFullscreen = false) => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={dataGrafico}
        margin={isFullscreen ? { top: 10, right: 30, left: 20, bottom: 60 } : { top: 0, right: 30, left: 10, bottom: 100 }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
        
        <XAxis 
          dataKey="name" 
          tickFormatter={isFullscreen ? undefined : formatXAxis}
          interval={0} 
          angle={isFullscreen ? 0 : -40} 
          textAnchor={isFullscreen ? "middle" : "end"}
          style={{ fontSize: isFullscreen ? '12px' : '11px', fontWeight: '500', fill: '#6c757d' }}
          dy={10}
        />
        
        <YAxis 
          tickFormatter={(value) => `€${value.toLocaleString()}`} 
          style={{ fontSize: '11px', fill: '#6c757d' }}
          axisLine={false}
          tickLine={false}
        />
        
        <RechartsTooltip 
          cursor={{ fill: '#f8f9fa' }}
          formatter={(value, name) => {
            const label = name === "importoEva" ? "Quantità evasa" : "Quantità da evadere";
            return [`€ ${value.toLocaleString()}`, label];
          }}
          contentStyle={{ 
            borderRadius: '12px', 
            border: 'none', 
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            padding: '10px' 
          }}
        />

        <Legend 
          verticalAlign="top" 
          height={36} 
          formatter={(value) => (value === "importoEva" ? "Quantità evasa" : "Quantità da evadere")}
        />
        
        {/* Usando stackId="a" le barre si sovrappongono formando un'unica barra divisa */}
        <Bar 
          dataKey="importoEva" 
          stackId="a" 
          fill="#dc2626" // Blu per EVA
          radius={0} 
          barSize={isFullscreen ? 50 : 35}
        />
        <Bar 
          dataKey="importoDev" 
          stackId="a" 
          fill="#6c757d" // Rosso per DEV
          radius={[6, 6, 0, 0]} // Arrotonda solo la parte superiore della barra totale
          barSize={isFullscreen ? 50 : 35}
        />
      </BarChart>
    </ResponsiveContainer>
  );

  if (loading) return <div className="text-center p-5 text-muted">Caricamento grafico in corso...</div>;

  return (
    <Card className="border-0 card shadow-lg overflow-hidden" style={{ borderRadius: '15px', height: '400px' }}>
      
      {/* HEADER DELLA CARD */}
      <div
        className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white"
        style={{ zIndex: 110 }}
      >
        <div className="d-flex align-items-center" onClick={() => setShowFullscreenModal(true)} style={{ cursor: "pointer" }}>
          <span
            style={{
              fontWeight: "800",
              fontSize: "0.75rem",
              color: "#1a1a1a",
              letterSpacing: "1px",
              marginRight: "8px",
            }}
          >
            TOP 10 - FORNITORI URGENTI (EVA vs DEV)
          </span>
          <Badge bg="dark" style={{ fontSize: "0.55rem", padding: "4px 6px" }}>ESPANDI</Badge>
          
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 200, hide: 200 }}
            overlay={renderTooltipSpiegazione}
          >
            <span className="ms-2" onClick={(e) => e.stopPropagation()} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
              <FiInfo size={14} color="#adb5bd" />
            </span>
          </OverlayTrigger>
        </div>
      </div>
      
      {/* CORPO DELLA CARD */}
      <div className="p-2 flex-grow-1" style={{ height: '370px' }}>
        {renderChartContent(false)}
      </div>

      {/* MODALE SCHERMO INTERO */}
      <Modal show={showFullscreenModal} onHide={() => setShowFullscreenModal(false)} fullscreen={true} animation={true}>
        <Modal.Body className="p-0 d-flex flex-column" style={{ background: "#f8f9fa" }}>
          <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm" style={{ zIndex: 110 }}>
            <div className="d-flex align-items-center">
              <span style={{ fontWeight: "800", fontSize: "0.9rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
                TOP 10 - FORNITORI URGENTI (VISTA ESPANSA)
              </span>
              <OverlayTrigger placement="bottom" overlay={renderTooltipSpiegazione}>
                <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}><FiInfo size={16} color="#adb5bd" /></span>
              </OverlayTrigger>
            </div>
            <Button variant="outline-dark" className="d-flex align-items-center gap-1 border-0" onClick={() => setShowFullscreenModal(false)} style={{ borderRadius: "10px", height: "38px" }}>
              <FiX size={16} /> Chiudi
            </Button>
          </div>
          
          <div className="flex-grow-1 p-4 d-flex align-items-center justify-content-center" style={{ overflowY: "auto" }}>
            <div className="bg-white p-4 shadow-sm rounded-4 border w-100" style={{ maxWidth: "1200px", height: "550px" }}>
              {renderChartContent(true)}
            </div>
          </div>
        </Modal.Body>
      </Modal>

    </Card>
  );
};

export default GraficoOrdiniFornitori;